import * as React from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Download, Loader2, Search } from 'lucide-react'
import type { LearningReportRow } from '@/types/learning.types'
import { PageTitle } from '@/components/PageTitle'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { ProgressRing } from '@/components/shared/ProgressRing'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useCompletionReport,
  useLearningCourses,
} from '@/hooks/repositories/useLearningRepository'
import { requirePermission } from '@/lib/permissions/route-guards'
import { LEARNING_PERMISSION_KEYS } from '@/lib/permissions/learning-permissions'
import { buildCsv } from '@/lib/csv-export'
import { downloadBlobAsFile } from '@/lib/download-blob'
import { useIsMobile } from '@/hooks/use-mobile'
import { formatDate } from '@/lib/formatters'

export const Route = createFileRoute('/_app/learning/admin/reports')({
  beforeLoad: requirePermission(LEARNING_PERMISSION_KEYS.reports),
  component: RouteComponent,
})

const EXPORT_HEADERS = [
  'Name',
  'Email',
  'Department',
  'Course',
  'Version',
  'Status',
  'Assigned',
  'Due',
  'Completed',
  'Lessons completed',
  'Lessons total',
] as const

/**
 * The export an examiner can read: who completed what, when, and which version.
 *
 * Version is a column rather than a footnote — an acknowledgment of v1 is not
 * evidence about v2, and a report that omits it invites exactly that
 * conflation.
 */
function toCsv(rows: Array<LearningReportRow>): string {
  return buildCsv(
    EXPORT_HEADERS,
    rows.map((row) => [
      row.userName,
      row.userEmail,
      row.department,
      row.courseTitle,
      `v${row.versionNumber}`,
      row.isOverdue && row.status !== 'COMPLETED' ? 'OVERDUE' : row.status,
      formatDate(row.assignedAt),
      row.dueAt ? formatDate(row.dueAt) : '',
      row.completedAt ? formatDate(row.completedAt) : '',
      String(row.lessonsCompleted),
      String(row.lessonCount),
    ]),
  )
}

function StatusCell({ row }: { row: LearningReportRow }) {
  if (row.status === 'COMPLETED') {
    return (
      <Badge
        variant="outline"
        className="border-emerald-500/30 bg-emerald-500/10 text-[10px] text-emerald-700 dark:text-emerald-400"
      >
        Complete
      </Badge>
    )
  }
  if (row.isOverdue) {
    return (
      <Badge
        variant="outline"
        className="border-destructive/40 bg-destructive/10 text-[10px] text-destructive"
      >
        Overdue
      </Badge>
    )
  }
  if (row.status === 'WAIVED') {
    return (
      <Badge variant="outline" className="text-[10px]">
        Excused
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="text-[10px]">
      {row.lessonsCompleted > 0 ? 'In progress' : 'Not started'}
    </Badge>
  )
}

/**
 * Completion reporting (sc-1245).
 *
 * Gated on `learning.reports` — this shows who across the company has and has
 * not done their training, which is a different and more sensitive thing than
 * writing a course.
 *
 * The rollup leads with what is NOT done. A completion percentage is the
 * comfortable number; overdue is the one with consequences.
 */
function RouteComponent() {
  const isMobile = useIsMobile()
  const { data: courses } = useLearningCourses()
  const [courseId, setCourseId] = React.useState<string>('all')
  const [search, setSearch] = React.useState('')

  const { data: rows, isPending } = useCompletionReport(
    courseId === 'all' ? undefined : courseId,
  )

  const filtered = React.useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return rows ?? []
    return (rows ?? []).filter(
      (row) =>
        row.userName.toLowerCase().includes(needle) ||
        row.userEmail.toLowerCase().includes(needle) ||
        row.courseTitle.toLowerCase().includes(needle),
    )
  }, [rows, search])

  const completed = filtered.filter((row) => row.status === 'COMPLETED').length
  const overdue = filtered.filter((row) => row.isOverdue).length
  const percent =
    filtered.length === 0 ? 0 : Math.round((completed / filtered.length) * 100)

  return (
    <div className="py-4">
      <PageTitle title="Learning — Reporting" />

      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link to="/learning/admin">
          <ArrowLeft className="mr-1 size-4" aria-hidden />
          Courses
        </Link>
      </Button>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
            Completion
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Who completed what, when, and which version.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={filtered.length === 0}
          onClick={() =>
            downloadBlobAsFile(
              new Blob([toCsv(filtered)], { type: 'text/csv;charset=utf-8' }),
              'learning-completion.csv',
            )
          }
        >
          <Download className="mr-1 size-4" aria-hidden />
          Export CSV
        </Button>
      </div>

      {/* ── Rollup ─────────────────────────────────────────────────────── */}
      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="flex items-center gap-4 py-5">
            <ProgressRing percent={percent} size={56} strokeWidth={5} />
            <div>
              <p className="text-xl font-bold tabular-nums">
                {completed}
                <span className="text-sm font-normal text-muted-foreground">
                  {' '}
                  / {filtered.length}
                </span>
              </p>
              <p className="text-sm text-muted-foreground">completed</p>
            </div>
          </CardContent>
        </Card>

        <Card className={overdue > 0 ? 'border-destructive/40' : undefined}>
          <CardContent className="py-5">
            <p className="text-xl font-bold tabular-nums">{overdue}</p>
            <p className="text-sm text-muted-foreground">overdue</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="py-5">
            <p className="text-xl font-bold tabular-nums">
              {filtered.length - completed - overdue}
            </p>
            <p className="text-sm text-muted-foreground">
              outstanding, in time
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <Card className="mb-4">
        <CardContent className="flex flex-col gap-3 py-3 sm:flex-row">
          <div className="relative flex-1">
            <Search
              className="absolute top-2.5 left-2.5 size-4 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search person or course"
              className="pl-8"
              aria-label="Search the report"
            />
          </div>
          <Select value={courseId} onValueChange={setCourseId}>
            <SelectTrigger className="sm:w-64" aria-label="Course">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All courses</SelectItem>
              {(courses ?? []).map((course) => (
                <SelectItem key={course.id} value={course.id}>
                  {course.publishedVersion?.title ??
                    course.draftVersion?.title ??
                    course.slug}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      {isPending ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-sm text-muted-foreground">
            Nothing to report yet. Assign a course to see completion here.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className={isMobile ? 'p-3' : 'p-0'}>
            {/* Seven columns do not survive a 375px screen. Below md the same
                rows become cards — the house pattern for dense tables, and the
                reason `useIsMobile` exists — rather than a horizontal scroll
                nobody discovers. */}
            {isMobile ? (
              <ul className="space-y-2">
                {filtered.map((row) => (
                  <li
                    key={row.assignmentId}
                    className="space-y-2 rounded-lg border p-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">
                          {row.userName}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {row.userEmail}
                        </p>
                      </div>
                      <StatusCell row={row} />
                    </div>

                    <p className="text-sm">{row.courseTitle}</p>

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <Badge variant="outline" className="text-[10px]">
                        v{row.versionNumber}
                      </Badge>
                      <span className="tabular-nums">
                        {row.lessonsCompleted}/{row.lessonCount} lessons
                      </span>
                      {row.dueAt && <span>Due {formatDate(row.dueAt)}</span>}
                      {row.completedAt && (
                        <span>Completed {formatDate(row.completedAt)}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Person</TableHead>
                      <TableHead>Course</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Due</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead className="text-right">Progress</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((row) => (
                      <TableRow key={row.assignmentId}>
                        <TableCell className="align-top">
                          <p className="font-medium">{row.userName}</p>
                          <p className="text-xs text-muted-foreground">
                            {row.userEmail}
                          </p>
                        </TableCell>
                        <TableCell className="align-top text-sm">
                          {row.courseTitle}
                        </TableCell>
                        <TableCell className="align-top">
                          {/* A column, not a footnote: an acknowledgment of v1 is
                            not evidence about v2. */}
                          <Badge variant="outline" className="text-[10px]">
                            v{row.versionNumber}
                          </Badge>
                        </TableCell>
                        <TableCell className="align-top">
                          <StatusCell row={row} />
                        </TableCell>
                        <TableCell className="align-top text-xs text-muted-foreground">
                          {row.dueAt ? formatDate(row.dueAt) : '—'}
                        </TableCell>
                        <TableCell className="align-top text-xs text-muted-foreground">
                          {row.completedAt ? formatDate(row.completedAt) : '—'}
                        </TableCell>
                        <TableCell className="align-top text-right text-xs tabular-nums">
                          {row.lessonsCompleted}/{row.lessonCount}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
